import { apiClient } from './client';
import type {
  Contract,
  ContractDetail,
  ContractDocumentSearchQuery,
  ContractDocumentSearchResult,
  ContractQuery,
  ContractsSummary,
  UpsertContract,
} from '@/types/contract';
import type { PagedResult } from '@/types/asset';

/**
 * Convert a {@link ContractQuery} filter object into query params for the contracts list endpoint.
 * @param query - Filter/pagination/sort parameters.
 * `statuses` is sent as a repeated key (`statuses=Active&statuses=Draft`), which ASP.NET binds to an array.
 * @returns URLSearchParams containing only the fields that were set.
 */
function toParams(query: ContractQuery): URLSearchParams {
  const params = new URLSearchParams({
    page: String(query.page),
    pageSize: String(query.pageSize),
  });

  if (query.search) params.set('search', query.search);
  if (query.status) params.set('status', query.status);
  query.statuses?.forEach((status) => params.append('statuses', status));
  if (query.contractType) params.set('contractType', query.contractType);
  if (query.supplier) params.set('supplier', query.supplier);
  if (query.activeOnly !== undefined) params.set('activeOnly', String(query.activeOnly));
  if (query.soonExpiring !== undefined) params.set('soonExpiring', String(query.soonExpiring));
  if (query.soonExpiringDays) params.set('soonExpiringDays', String(query.soonExpiringDays));
  if (query.viewMode) params.set('viewMode', query.viewMode);
  if (query.noticeLeadMinMonths !== undefined) params.set('noticeLeadMinMonths', String(query.noticeLeadMinMonths));
  if (query.noticeLeadMaxMonths !== undefined) params.set('noticeLeadMaxMonths', String(query.noticeLeadMaxMonths));
  if (query.endingSoonMinMonths !== undefined) params.set('endingSoonMinMonths', String(query.endingSoonMinMonths));
  if (query.endingSoonMaxMonths !== undefined) params.set('endingSoonMaxMonths', String(query.endingSoonMaxMonths));
  if (query.sortBy) params.set('sortBy', query.sortBy);
  if (query.sortDescending) params.set('sortDescending', String(query.sortDescending));

  return params;
}

/** API client for supplier contracts, their documents, and dashboard summary. */
export const contractsApi = {
  /**
   * List contracts matching the given filters, paginated.
   * @param query - Filter/pagination/sort parameters.
   * @returns A page of matching contracts.
   */
  async list(query: ContractQuery): Promise<PagedResult<Contract>> {
    const { data } = await apiClient.get<PagedResult<Contract>>('/api/SupplierContracts', {
      params: toParams(query),
    });
    return data;
  },

  /**
   * Fetch full details for a single contract.
   * @param id - Contract id.
   * @returns The contract detail.
   */
  async get(id: number): Promise<ContractDetail> {
    const { data } = await apiClient.get<ContractDetail>(`/api/SupplierContracts/${id}`);
    return data;
  },

  /**
   * Fetch contract dashboard summary metrics for a period.
   * @param periodStart - ISO start date of the period.
   * @param periodEnd - ISO end date of the period.
   * @param currency - Optional currency code to compute recurring value in.
   * @returns The summary metrics.
   * @throws If the response is missing expected fields, indicating the backend
   * needs to be restarted/redeployed to a version matching this client.
   */
  async summary(periodStart: string, periodEnd: string, currency?: string): Promise<ContractsSummary> {
    const { data } = await apiClient.get<ContractsSummary>('/api/SupplierContracts/summary', {
      params: { periodStart, periodEnd, currency },
    });

    // Guards against silently rendering a stale/incompatible dashboard when an
    // older backend deployment omits these fields rather than returning zero.
    if (
      !data.totalContracts ||
      !data.activeContracts ||
      !data.incompleteContracts ||
      !data.recurringAnnualValue
    ) {
      throw new Error('The contract dashboard API is outdated. Restart or redeploy the backend and try again.');
    }

    return data;
  },

  /**
   * Search contract documents across all contracts, paginated.
   * @param query - Search text, supplier filter, and pagination.
   * @returns Matching documents.
   */
  async searchDocuments(query: ContractDocumentSearchQuery): Promise<ContractDocumentSearchResult> {
    const { data } = await apiClient.get<ContractDocumentSearchResult>('/api/SupplierContracts/documents/search', {
      params: {
        search: query.search,
        supplier: query.supplier,
        page: query.page,
        pageSize: query.pageSize,
      },
    });
    return data;
  },

  /**
   * Create a new contract, optionally uploading documents alongside it.
   * @param payload - Contract fields, including cost lines.
   * @param documents - Optional files to attach to the new contract.
   * @returns The created contract's details.
   */
  async create(payload: UpsertContract, documents?: File[]): Promise<ContractDetail> {
    const formData = new FormData();

    formData.append('title', payload.title);
    formData.append('isComplete', String(payload.isComplete));
    formData.append('supplierName', payload.supplierName);
    formData.append('startDate', payload.startDate);
    if (payload.endDate) {
      formData.append('endDate', payload.endDate);
    }
    formData.append('contractType', payload.contractType);
    formData.append('currency', payload.currency);
    formData.append('noticePeriodUnit', payload.noticePeriodUnit);
    formData.append('status', payload.status);
    if (payload.noticePeriodDays !== null && payload.noticePeriodDays !== undefined) {
      formData.append('noticePeriodDays', String(payload.noticePeriodDays));
    }
    if (payload.notes) {
      formData.append('notes', payload.notes);
    }
    payload.costLines.forEach((line, index) => {
      formData.append(`costLines[${index}].label`, line.label);
      formData.append(`costLines[${index}].quantity`, String(line.quantity));
      formData.append(`costLines[${index}].unitPrice`, String(line.unitPrice));
      formData.append(`costLines[${index}].period`, line.period);
      formData.append(`costLines[${index}].currency`, line.currency);
    });

    documents?.forEach((file) => formData.append('documents', file, file.name));

    const { data } = await apiClient.post<ContractDetail>('/api/SupplierContracts', formData);
    return data;
  },

  /**
   * Update an existing contract.
   * @param id - Contract id.
   * @param payload - Full replacement of the contract's editable fields.
   * @returns The updated contract's details.
   */
  async update(id: number, payload: UpsertContract): Promise<ContractDetail> {
    const { data } = await apiClient.put<ContractDetail>(`/api/SupplierContracts/${id}`, payload);
    return data;
  },

  /**
   * Upload an additional document to an existing contract.
   * @param id - Contract id.
   * @param document - File to upload.
   */
  async uploadDocument(id: number, document: File): Promise<void> {
    const formData = new FormData();
    formData.append('document', document, document.name);
    await apiClient.post(`/api/SupplierContracts/${id}/documents`, formData);
  },

  /**
   * Download a contract document's binary content.
   * @param contractId - Contract id.
   * @param documentId - Document id.
   * @returns The document content as a Blob.
   */
  async downloadDocument(contractId: number, documentId: number): Promise<Blob> {
    const { data } = await apiClient.get(`/api/SupplierContracts/${contractId}/documents/${documentId}/content`, {
      responseType: 'blob',
    });
    return data as Blob;
  },

  /**
   * Delete a document from a contract.
   * @param contractId - Contract id.
   * @param documentId - Document id.
   */
  async deleteDocument(contractId: number, documentId: number): Promise<void> {
    await apiClient.delete(`/api/SupplierContracts/${contractId}/documents/${documentId}`);
  },

  /**
   * Rename a contract document.
   * @param contractId - Contract id.
   * @param documentId - Document id.
   * @param fileName - New file name.
   */
  async renameDocument(contractId: number, documentId: number, fileName: string): Promise<void> {
    await apiClient.patch(`/api/SupplierContracts/${contractId}/documents/${documentId}/name`, { fileName });
  },

  /**
   * Delete a contract.
   * @param id - Contract id.
   */
  async remove(id: number): Promise<void> {
    await apiClient.delete(`/api/SupplierContracts/${id}`);
  },
};
